import type Decimal from 'decimal.js'

export type DigitStyle = 'latn' | 'arab'

export interface FormatOptions {
  locale: string
  digits?: DigitStyle
  minimumFractionDigits?: number
  maximumFractionDigits?: number
  signDisplay?: 'auto' | 'always' | 'exceptZero'
}

const cache = new Map<string, Intl.NumberFormat>()

function formatter(opts: FormatOptions): Intl.NumberFormat {
  const key = JSON.stringify(opts)
  let f = cache.get(key)
  if (!f) {
    f = new Intl.NumberFormat(opts.locale, {
      numberingSystem: opts.digits ?? 'latn',
      minimumFractionDigits: opts.minimumFractionDigits,
      maximumFractionDigits: opts.maximumFractionDigits,
      signDisplay: opts.signDisplay,
    })
    cache.set(key, f)
  }
  return f
}

/**
 * Formats a decimal without passing through a JS float, so large or very
 * precise values are not altered (Intl accepts exact decimal strings).
 */
export function formatDecimal(value: Decimal | string | number, opts: FormatOptions): string {
  const str = typeof value === 'object' ? value.toFixed() : String(value)
  return formatter(opts).format(str as Intl.StringNumericLiteral)
}

/** Formats with a fixed number of fraction digits. */
export function formatFixed(
  value: Decimal | string | number,
  fractionDigits: number,
  opts: Omit<FormatOptions, 'minimumFractionDigits' | 'maximumFractionDigits'>,
): string {
  return formatDecimal(value, {
    ...opts,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })
}
